import { Heart, MapPin, Star } from "lucide-react";
import { DsIcon } from "@/components/ui/ds-icons";
import { cn, isValidIsoDate } from "@/lib/utils";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CarImagePlaceholder } from "@/components/cars/CarImagePlaceholder";
import { useCurrency } from "@/i18n/CurrencyContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { useFavourite } from "@/hooks/useFavourite";
import { SocialProofBadges } from "@/components/cars/SocialProofBadges";
import { ProOwnerBadge } from "@/components/cars/ProOwnerBadge";
import { ActiveHostBadge } from "@/components/cars/ActiveHostBadge";
import { CarCardImageCarousel, type CarCardImage } from "@/components/cars/CarCardImageCarousel";
import type { CarCardData } from "@/lib/api";
import { carNavigationPath } from "@/lib/car-navigation-path";
import { useLocale } from "@/i18n/LocaleContext";
import { getDisplayPrice, type RentalType } from "@/lib/displayPrice";
import driveShareLogo from "@assets/driveshare-green-logo_1756734944745-De_wRc_a_1781716449728.png";
export type { CarCardData } from "@/lib/api";

interface CarCardProps {
  car: CarCardData;
  className?: string;
  priority?: boolean;
  rentalType?: RentalType;
  dateFrom?: string;
  dateTo?: string;
  onHoverChange?: (isHovered: boolean) => void;
  // When true, the card is the one matching the currently-selected map pin.
  // Applies a brand-coloured ring so the result list highlights the pin the
  // user just selected on the map (reverse of the hover→pin sync).
  selected?: boolean;
  // `sizes` hint for the MOBILE carousel srcset (the carousel is hidden at
  // sm:+). Defaults to 100vw (full-width card, e.g. search list); homepage
  // rails render cards 2-up on phones and pass ~50vw so the browser fetches
  // the 800w variant instead of the 2400w full (Task #6465).
  mobileImageSizes?: string;
  /** Only supplied by public inventory with exact-URL binary measurements. */
  measuredImages?: boolean;
}

// TODO: When image selection logic is available, prioritise exterior shots as the primary image
function CarCardImpl({ car, className, priority, rentalType, dateFrom, dateTo, onHoverChange, selected, mobileImageSizes, measuredImages }: CarCardProps) {
  const { t } = useTranslation();
  const { language } = useLocale();
  const instantBookEnabled = useFeatureFlag("feature_instant_book");
  // Task #4153 follow-on: when the signpost flag is ON, US cars stay visible in
  // search/rails but carry a "Book on DRIVESHARE" badge steering renters to the
  // partner (the car-detail page still shows the full handoff card).
  const usSignpostEnabled = useFeatureFlag("feature_us_search_signpost");
  const driveshareCutover = useFeatureFlag("brand_cutover_driveshare");
  const showUsSignpost = !driveshareCutover && usSignpostEnabled && car.countryCode === "US";
  const { isFavourited: isFav, toggle: toggleFav, loading: favLoading } = useFavourite(car.id, {
    make: car.make,
    model: car.model,
    year: car.year,
    slug: car.slug,
    city: car.city ?? null,
    state: car.state ?? null,
    country_code: car.countryCode ?? null,
    primary_image:
      car.primaryImageCdnMedium ??
      car.primaryImageCdnFull ??
      car.primaryImageUrl ??
      null,
    price_driving: car.priceDriving ?? null,
    price_event: car.priceEvent ?? null,
    minimum_price: car.minimumPrice ?? null,
    chauffeur_min_price: car.chauffeurMinPrice ?? null,
    event_min_price: car.eventMinPrice ?? null,
    rental_driving_active: car.rentalDrivingActive,
    rental_chauffeured_active: car.rentalChauffeurActive,
    rental_event_active: car.rentalEventActive,
    local_currency_code: car.currencyCode ?? null,
  });
  const title = `${car.year} ${car.make} ${car.model}`;
  const measuredPrimary = measuredImages
    ? car.images?.find(image => image.url === (car.primaryImageCdnMedium || car.primaryImageUrl))
    : undefined;
  // Legacy listings whose only photo origin is a dead 404 would otherwise leave
  // a broken/grey tile here; on load failure we render a neutral placeholder.
  const [imgError, setImgError] = useState(false);
  const location = [car.city, car.state].filter(Boolean).join(", ");
  // A curated Featured listing takes precedence over the score-driven
  // Standout badge so the card never shows both labels at once.
  const showStandout = !car.featured && car.totalScore != null && car.totalScore > 70;
  // Task #2342 — browsing surfaces render in the renter's preferred
  // currency only (no dual display, no `≈` marker). `formatPreferred`
  // converts the listing's local currency into the active display
  // currency via the shared FX table; if no rate exists we fall back
  // to the source currency so the card never shows a blank price.
  const { formatPreferred } = useCurrency();
  const priceInfo = getDisplayPrice(car, rentalType);
  const displayPrice = priceInfo.price;
  const displayPriceLabel = displayPrice != null && displayPrice > 0
    ? formatPreferred(displayPrice, car.currencyCode || "USD")
    : null;
  const perHourLabel = t("common.per_hour", { defaultValue: "/hr" });
  // Primary "from" suffix. No-rental-type mode returns a semantic `unit`:
  // the daily/minimum "from" headline shows the amount only (no "/ day"),
  // while an hourly-only listing keeps "/hr" (it has no secondary line to
  // clarify the unit). Per-rental-type mode keeps the legacy raw suffix
  // ("/day" | "") unchanged.
  const priceSuffix = priceInfo.unit
    ? priceInfo.unit === "hour"
      ? perHourLabel
      : ""
    : priceInfo.suffix;
  // Secondary muted hourly line (no-rental-type mode, and chauffeur/event
  // filtered modes when the mode's hourly rate is available — task #6107).
  const hourlyLabel =
    priceInfo.hourlyPrice != null && priceInfo.hourlyPrice > 0
      ? formatPreferred(priceInfo.hourlyPrice, car.currencyCode || "USD")
      : null;
  const transmissionLabel = car.transmission
    ? car.transmission.charAt(0).toUpperCase() + car.transmission.slice(1).toLowerCase()
    : null;

  const hasRentalIcons = car.rentalDrivingActive || car.rentalChauffeurActive || car.rentalEventActive;
  const path = carNavigationPath(car, language);
  const selection = new URLSearchParams();
  if (isValidIsoDate(dateFrom)) selection.set("from", dateFrom!);
  if (isValidIsoDate(dateTo)) selection.set("to", dateTo!);
  if (rentalType) selection.set("type", rentalType);
  const selectionQuery = selection.size ? `?${selection}` : "";

  // Mobile carousel image set. Prefer the full `images` array (home rails +
  // search, primary first); fall back to the single primary image fields so
  // sources that only carry a primary image still render a single slide.
  const cardImages: CarCardImage[] =
    car.images && car.images.length > 0
      ? car.images
      : car.primaryImageUrl
        ? [
            {
              url: car.primaryImageUrl,
              cdnThumbnail: car.primaryImageCdnThumbnail ?? null,
              cdnMedium: car.primaryImageCdnMedium ?? null,
              cdnFull: car.primaryImageCdnFull ?? null,
            },
          ]
        : [];

  return (
    <a
      href={path ? `${import.meta.env.BASE_URL}${path.replace(/^\//, "")}${selectionQuery}` : undefined}
      aria-disabled={!path || undefined}
      data-link-unavailable={!path || undefined}
      target="_blank"
      rel="noopener noreferrer"
      data-car-id={car.id}
      data-car-country={car.countryCode}
      data-selected={selected ? "true" : undefined}
      onMouseEnter={onHoverChange ? () => onHoverChange(true) : undefined}
      onMouseLeave={onHoverChange ? () => onHoverChange(false) : undefined}
      onFocus={onHoverChange ? () => onHoverChange(true) : undefined}
      onBlur={onHoverChange ? () => onHoverChange(false) : undefined}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-2xl bg-background border border-border shadow-sm transition-shadow duration-300 hover:shadow-xl",
        // Reverse-sync highlight: when the matching map pin is selected, ring
        // the card in the brand colour. Uses ring (independent of the hover
        // shadow) so it never fights the existing hover→pin highlight.
        selected && "ring-2 ring-brand ring-offset-2 ring-offset-background shadow-xl",
        className,
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {/* Mobile only: swipeable in-card photo carousel. */}
        <CarCardImageCarousel
          images={cardImages}
          title={title}
          priority={priority}
          noImageLabel={t("common.no_image")}
          className="sm:hidden"
          sizes={mobileImageSizes}
          measuredImages={measuredImages}
        />
        {/* Tablet/desktop (sm:+): unchanged single image with hover zoom.
            `hidden sm:block` is byte-identical at sm:+ because Tailwind
            preflight already renders <img> as display:block. */}
        {car.primaryImageUrl && !imgError ? (
          <img
            src={car.primaryImageCdnMedium || car.primaryImageUrl}
            width={measuredPrimary?.width ?? undefined}
            height={measuredPrimary?.height ?? undefined}
            srcSet={
              car.primaryImageCdnThumbnail || car.primaryImageCdnMedium || car.primaryImageCdnFull
                ? [
                    car.primaryImageCdnThumbnail && `${car.primaryImageCdnThumbnail} 400w`,
                    car.primaryImageCdnMedium && `${car.primaryImageCdnMedium} 800w`,
                    car.primaryImageCdnFull && `${car.primaryImageCdnFull} 2400w`,
                  ].filter(Boolean).join(", ")
                : undefined
            }
            alt={title}
            loading={priority ? "eager" : "lazy"}
            {...(priority ? { fetchPriority: "high" as const } : {})}
            // Below sm this <img> is display:none (the mobile carousel renders
            // instead), but Chromium still fetches hidden lazy images — and a
            // `100vw` mobile clause at DPR3 resolved to the 2400w original
            // (~500KB × every card, live-audited in Task #7873). `1px` makes the
            // hidden fetch pick the smallest (400w) candidate instead.
            sizes="(max-width: 639px) 1px, (max-width: 1024px) 50vw, 33vw"
            className="hidden sm:block h-full w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-[1.02]"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="hidden h-full w-full sm:block">
            <CarImagePlaceholder label={t("common.no_image")} />
          </div>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void toggleFav();
          }}
          disabled={favLoading}
          className="touch-44 absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full material-chip max-sm:bg-transparent! max-sm:backdrop-blur-none! transition-colors hover:bg-background disabled:opacity-70"
          aria-label={isFav ? t("common.remove_from_favorites") : t("common.add_to_favorites")}
          aria-pressed={isFav}
        >
          {/* Keep the visible circle compact; the mobile button remains a
              transparent 44px tap target around this 32px surface. */}
          <span className="contents max-sm:flex max-sm:h-8 max-sm:w-8 max-sm:items-center max-sm:justify-center max-sm:rounded-full max-sm:bg-white">
            <Heart
              className={cn(
                "h-4 w-4 transition-colors",
                isFav ? "fill-red-500 text-red-500" : "text-muted-foreground max-sm:text-foreground",
              )}
            />
          </span>
        </button>

        {/* Natural-width badges wrap independently; longer translations can
            wrap inside a pill. Reserve the mobile heart's full 44px target
            plus an 8px gap. Featured still takes precedence over Standout. */}
        {(
          <div data-testid="car-recognition-badges" className="absolute left-3 right-16 top-3 z-10 flex flex-wrap items-start gap-1.5">
            {car.featured && (
              <span className="inline-flex max-w-full shrink-0 items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-brand-foreground shadow-sm">
                <Star className="h-2.5 w-2.5 shrink-0 fill-current" />
                <span className="min-w-0 break-words">{t("common.featured")}</span>
              </span>
            )}
            {showStandout && (
              <span className="inline-flex max-w-full shrink-0 items-center rounded-full material-chip px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-foreground">
                <span className="min-w-0 break-words">{t("common.standout")}</span>
              </span>
            )}
            <ProOwnerBadge ownerProTier={car.ownerProTier} />
            <ActiveHostBadge carId={car.id} />
          </div>
        )}

        {showUsSignpost && (
          <div className="absolute bottom-3 left-3 z-10">
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0f5132] px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm"
              data-testid="us-signpost-badge"
            >
              {t("usRedirect.signpost_label")}
              <img
                src={driveShareLogo}
                alt={t("usRedirect.logo_alt")}
                className="h-3 w-auto brightness-0 invert"
              />
            </span>
          </div>
        )}

      </div>

      <div className="flex flex-1 flex-col p-4">
        {!path && <p role="status" className="text-sm text-muted-foreground">
          {t("carCard.link_unavailable", { defaultValue: "Listing link temporarily unavailable" })}
        </p>}
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold leading-tight text-foreground line-clamp-1">
            {title}
          </h3>
        </div>
        <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span>{location}</span>
        </div>

        {displayPriceLabel && (
          <div className="mt-1.5">
            <p className="text-sm font-bold text-brand">
              {t("common.from_price", { defaultValue: "from" })} {displayPriceLabel}{priceSuffix}
            </p>
            {hourlyLabel && (
              <p className="text-xs font-normal text-muted-foreground">
                {hourlyLabel}{perHourLabel}
              </p>
            )}
          </div>
        )}

        <SocialProofBadges
          className="mt-2"
          variant="inline"
          rating={car.reviewsRating}
          reviewCount={car.reviewsCount}
          instantBook={instantBookEnabled && car.instantBook}
          showPopular={false}
          showNewListing={false}
        />
      </div>

      <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
        <div className="flex items-center gap-3">
          {hasRentalIcons && (
            <div className="flex items-center gap-2">
              {car.rentalDrivingActive && (
                <DsIcon name="driveable" className="h-5 w-5 text-muted-foreground" />
              )}
              {car.rentalEventActive && (
                <DsIcon name="event-rental" className="h-5 w-5 text-muted-foreground" />
              )}
              {car.rentalChauffeurActive && (
                <DsIcon name="chauffeured" className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          )}
          {!hasRentalIcons && transmissionLabel && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <DsIcon name="driveable" className="h-3.5 w-3.5" />
              <span>{transmissionLabel}</span>
            </div>
          )}
        </div>
        {displayPrice == null || displayPrice <= 0 ? (
          <span className="text-sm font-medium text-muted-foreground">
            {t("common.contact_for_price", { defaultValue: "Contact for price" })}
          </span>
        ) : null}
      </div>
    </a>
  );
}

// Memoise so the 12 cards on /search don't all re-render every time the
// parent updates state (e.g. setHoveredCarId, opening the filter drawer,
// switching grid/map view). The `cars` array from useAlgoliaSearchResults
// is itself memoised so each `car` prop keeps a stable reference between
// non-search renders, letting strict equality short-circuit cheaply.
//
// `onHoverChange` is intentionally excluded from the comparator: callers
// pass a fresh closure each render, but those closures only capture
// `car.id` (stable per card) and React's setState setter (stable across
// renders), so a stale callback ref behaves identically to a fresh one.
export const CarCard = memo(CarCardImpl, (prev, next) =>
  prev.car === next.car &&
  prev.className === next.className &&
  prev.priority === next.priority &&
  prev.rentalType === next.rentalType &&
  prev.dateFrom === next.dateFrom &&
  prev.dateTo === next.dateTo &&
  prev.selected === next.selected &&
  prev.mobileImageSizes === next.mobileImageSizes
);

export function CarCardSkeleton({
  className,
  featured = false,
}: {
  className?: string;
  featured?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t("carCard.loading_aria")}
      className={cn(
        "relative flex h-full flex-col overflow-hidden rounded-2xl bg-background border border-border shadow-sm",
        className,
      )}
    >
      {/* Image area — matches CarCard's aspect-[4/3] exactly to avoid layout shift */}
      <div className="relative aspect-[4/3] bac-shimmer">
        {/* Faint heart pill placeholder, top-right (mirrors favorite button) */}
        <div className="absolute right-3 top-3 h-8 w-8 rounded-full material-chip max-sm:bg-white! max-sm:backdrop-blur-none!" />
        {/* Optional featured pill placeholder, top-left */}
        {featured && (
          <div className="absolute left-3 top-3 h-5 w-16 rounded-full material-chip" />
        )}
      </div>

      {/* Content block — same paddings and gaps as CarCard */}
      <div className="flex flex-1 flex-col p-4">
        {/* Title line */}
        <div className="bac-shimmer h-5 w-3/4 rounded-md" />

        {/* Location row — small dot icon + text bar */}
        <div className="mt-1 flex items-center gap-1.5">
          <div className="bac-shimmer h-3.5 w-3.5 rounded-full" />
          <div className="bac-shimmer h-3.5 w-1/2 rounded-md" />
        </div>

        {/* Price line */}
        <div className="bac-shimmer mt-2 h-4 w-2/5 rounded-md" />

        {/* Social-proof row — a few small pill placeholders */}
        <div className="mt-2.5 flex items-center gap-1.5">
          <div className="bac-shimmer h-4 w-14 rounded-full" />
          <div className="bac-shimmer h-4 w-10 rounded-full" />
        </div>
      </div>

      {/* Bottom meta row — divider + two small placeholders */}
      <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <div className="bac-shimmer h-4 w-4 rounded-full" />
          <div className="bac-shimmer h-4 w-4 rounded-full" />
        </div>
        <div className="bac-shimmer h-3 w-16 rounded-md" />
      </div>

      <span className="sr-only">{t("carCard.loading_ellipsis")}</span>
    </div>
  );
}
